import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { TrendingDown, TrendingUp, Minus, Target } from "lucide-react";

const currency = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;

export default function RefinanceModeler({ loan, open, onOpenChange }) {
  const [form, setForm] = useState({
    new_rate: 5.5,
    new_term_years: 25,
    closing_costs: 10000,
    new_loan_type: "amortizing",
    new_io_period_months: 0,
    extra_principal: 0,
  });
  const [scenario, setScenario] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);

  const run = async () => {
    if (!loan) return;
    setBusy(true);
    try {
      const { data } = await api.post("/loans/refinance-scenario", {
        loan_id: loan.id,
        new_rate: Number(form.new_rate),
        new_term_years: Number(form.new_term_years),
        closing_costs: Number(form.closing_costs),
        new_loan_type: form.new_loan_type,
        new_io_period_months: Number(form.new_io_period_months) || 0,
        extra_principal: Number(form.extra_principal) || 0,
      });
      setScenario(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Calculation failed");
    } finally { setBusy(false); }
  };

  const saveAsPlan = async () => {
    if (!loan || !scenario) return;
    const extra = Number(form.extra_principal) || 0;
    const confirmMsg = extra > 0
      ? `Save as target plan?\n\nPaying $${extra.toLocaleString()}/mo extra principal will pay the loan off in ${scenario.extra_payoff?.payoff_months || "?"} months (${scenario.extra_payoff?.months_saved || 0} earlier) and save $${(scenario.extra_payoff?.interest_saved || 0).toLocaleString(undefined,{maximumFractionDigits:0})} in interest.`
      : "Save this refinance as your target payoff plan? Progress will be tracked on the Loan Payments dialog.";
    if (!window.confirm(confirmMsg)) return;
    setSavingPlan(true);
    try {
      await api.post(`/loans/${loan.id}/plan`, {
        new_rate: Number(form.new_rate),
        new_term_years: Number(form.new_term_years),
        new_term_months: 0,
        new_loan_type: form.new_loan_type,
        new_io_period_months: Number(form.new_io_period_months) || 0,
        extra_principal: extra,
        closing_costs: Number(form.closing_costs) || 0,
        source: "refinance_modeler",
      });
      toast.success("Target plan saved. Open Loan Payments to track progress.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save plan");
    } finally { setSavingPlan(false); }
  };

  if (!loan) return null;

  const savings = scenario?.analysis?.monthly_savings ?? 0;
  const positive = savings > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle style={{fontFamily:"Outfit"}}>Refinance Scenario — {loan.loan_number}</DialogTitle>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs font-mono mb-4 flex justify-between">
          <span>{loan.property_address}</span>
          <span>Current: {currency(loan.loan_amount)} @ {loan.interest_rate}% / {loan.term_years}y</span>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="New rate (%)">
            <input type="number" step="0.01" className="input-clean w-full" data-testid="refi-rate-input" value={form.new_rate} onChange={(e)=>setForm({...form, new_rate:e.target.value})} />
          </Field>
          <Field label="New term (yrs)">
            <input type="number" className="input-clean w-full" data-testid="refi-term-input" value={form.new_term_years} onChange={(e)=>setForm({...form, new_term_years:e.target.value})} />
          </Field>
          <Field label="Closing costs ($)">
            <input type="number" className="input-clean w-full" data-testid="refi-closing-input" value={form.closing_costs} onChange={(e)=>setForm({...form, closing_costs:e.target.value})} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="New loan type">
            <select
              data-testid="refi-type-select"
              className="input-clean w-full"
              value={form.new_loan_type}
              onChange={(e) => setForm({
                ...form,
                new_loan_type: e.target.value,
                new_io_period_months: e.target.value === "amortizing" ? 0 : form.new_io_period_months,
              })}
            >
              <option value="amortizing">Amortizing (P&I)</option>
              <option value="interest_only">Interest-only</option>
            </select>
          </Field>
          {form.new_loan_type === "interest_only" && (
            <Field label="IO months (0 = full term + balloon)">
              <input
                type="number" min="0"
                className="input-clean w-full"
                data-testid="refi-io-period-input"
                value={form.new_io_period_months}
                onChange={(e)=>setForm({...form, new_io_period_months:e.target.value})}
              />
            </Field>
          )}
          <Field label="Extra principal / month ($)">
            <input
              type="number" min="0" step="50"
              className="input-clean w-full"
              data-testid="refi-extra-principal-input"
              value={form.extra_principal}
              onChange={(e)=>setForm({...form, extra_principal:e.target.value})}
              placeholder="0"
            />
          </Field>
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={run} disabled={busy} data-testid="refi-run-button" className="btn-primary-dark disabled:opacity-60">
            {busy ? "Modeling…" : "Run scenario"}
          </button>
        </div>

        {scenario && (
          <div className="mt-6 space-y-4" data-testid="refi-results">
            {/* Side by side */}
            <div className="grid grid-cols-2 gap-3">
              <Panel title="Current loan">
                <Row label="Balance" value={currency(scenario.current.balance)} />
                <Row label="Monthly P&I" value={currency(scenario.current.monthly_payment)} />
                <Row label="Rate" value={`${scenario.current.rate}%`} />
                <Row label="Remaining months" value={scenario.current.remaining_months} />
                <Row label="Remaining interest" value={currency(scenario.current.remaining_interest)} />
              </Panel>
              <Panel title="Refinance" dark>
                <Row label="Principal" value={currency(scenario.refinance.principal)} dark />
                <Row label="Type" value={scenario.refinance.loan_type === "interest_only" ? "Interest-only" : "Amortizing"} dark />
                <Row label={scenario.refinance.loan_type === "interest_only" ? "Monthly IO payment" : "Monthly P&I"} value={currency(scenario.refinance.monthly_payment)} dark />
                {scenario.refinance.loan_type === "interest_only" && scenario.refinance.io_period_months > 0 && scenario.refinance.amortizing_payment > 0 && (
                  <Row label={`After ${scenario.refinance.io_period_months}mo IO`} value={currency(scenario.refinance.amortizing_payment) + "/mo"} dark />
                )}
                {scenario.refinance.balloon_payment > 0 && (
                  <Row label="Balloon at maturity" value={currency(scenario.refinance.balloon_payment)} dark />
                )}
                <Row label="Rate" value={`${scenario.refinance.rate}%`} dark />
                <Row label="Term" value={`${scenario.refinance.term_years} years`} dark />
                <Row label="Total interest" value={currency(scenario.refinance.total_interest)} dark />
              </Panel>
            </div>

            {/* Verdict */}
            <div className={`rounded-sm p-5 border ${positive ? "bg-green-50 border-green-300" : savings === 0 ? "bg-slate-50 border-slate-200" : "bg-red-50 border-red-300"}`} data-testid="refi-verdict">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className={`overline ${positive ? "text-green-700" : savings === 0 ? "text-slate-500" : "text-red-700"} flex items-center gap-1`}>
                    {positive ? <TrendingDown className="w-3 h-3" /> : savings === 0 ? <Minus className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                    {positive ? "Monthly savings" : savings === 0 ? "No change" : "Payment increases"}
                  </div>
                  <div className={`mt-2 text-4xl font-bold font-mono ${positive ? "text-green-700" : savings === 0 ? "text-slate-500" : "text-red-700"}`}>
                    {currency(Math.abs(savings))}/mo
                  </div>
                </div>
                <div className="text-right">
                  <div className="overline text-slate-500">Lifetime savings</div>
                  <div className={`mt-2 text-2xl font-bold font-mono ${scenario.analysis.lifetime_savings > 0 ? "text-green-700" : "text-red-700"}`}>
                    {currency(scenario.analysis.lifetime_savings)}
                  </div>
                  {scenario.analysis.breakeven_months != null && (
                    <div className="text-xs text-slate-500 font-mono mt-1">
                      Break-even: {scenario.analysis.breakeven_months} months
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200/50 text-sm font-semibold text-slate-900">
                {scenario.analysis.recommendation}
              </div>
            </div>

            {scenario.extra_payoff && (
              <div className="rounded-sm p-5 border bg-indigo-50 border-indigo-300" data-testid="refi-extra-payoff">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="overline text-indigo-700 flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" />
                      With {currency(scenario.extra_payoff.extra_principal)} / mo extra principal
                    </div>
                    <div className="mt-2 text-sm text-indigo-900 font-mono">
                      Paid off in <b data-testid="refi-extra-payoff-months">{scenario.extra_payoff.payoff_months}</b> months
                      &nbsp;•&nbsp; <b data-testid="refi-extra-months-saved">{scenario.extra_payoff.months_saved}</b> months earlier
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="overline text-slate-500">Interest saved</div>
                    <div className="mt-1 text-2xl font-bold font-mono text-green-700" data-testid="refi-extra-interest-saved">
                      {currency(scenario.extra_payoff.interest_saved)}
                    </div>
                    <div className="text-xs font-mono text-slate-500 mt-1">
                      Total paid: {currency(scenario.extra_payoff.total_paid)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {scenario && (
            <button
              onClick={saveAsPlan}
              disabled={savingPlan}
              data-testid="refi-save-plan-button"
              className="btn-primary-dark text-sm px-4 py-2 mr-auto flex items-center gap-2 disabled:opacity-60"
            >
              <Target className="w-4 h-4" />
              {savingPlan ? "Saving plan…" : "Save as target plan"}
            </button>
          )}
          <button onClick={() => onOpenChange(false)} className="btn-outline-dark">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>;
}
function Panel({ title, children, dark }) {
  return (
    <div className={`rounded-sm border p-4 ${dark ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>
      <div className={`overline ${dark ? "text-white/60" : "text-slate-500"} mb-3`}>{title}</div>
      {children}
    </div>
  );
}
function Row({ label, value, dark }) {
  return (
    <div className={`flex items-center justify-between py-1.5 text-sm ${dark ? "" : ""}`}>
      <span className={dark ? "text-white/70" : "text-slate-600"}>{label}</span>
      <span className={`font-mono font-bold ${dark ? "text-white" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}
